import * as _ from 'lodash';
import * as d3 from 'd3';

import { Helper } from '../../../../common/helpers';
import { Dictionary, DigitalCorrespondanceLayer, DigitalCorrespondanceTable, DigitalCorrespondanceTableRow, DigitalSet } from '@models/index';
import { guidEmpty } from '../../../../common/constants';
import { TreeRowViewModel } from './TreeRowViewModel';

/**
 * Builds the list of rows to display in edit mode.
 * @param table The original table configuration.
 * @param digitalSets The complete dictionary of digital sets.
 * @returns The list of association table rows to display.
 */
export function getRows(
    table: DigitalCorrespondanceTable,
    digitalSets: Dictionary<DigitalSet>) {
    const { associationTable, layers } = table;
    const existingParents = associationTable.map(d => d.parentRowGuid);
    let allAssociations = [];
    // If no child with empty guid, the tree is empty
    if (existingParents.indexOf(guidEmpty) < 0) {
        // Add root elements if no association at all
        const layer = layers.filter(d => d.level === 0);
        const values = getChildrenAssociations(layer, digitalSets, guidEmpty);
        if (values.length) {
            allAssociations = allAssociations.concat(values);
        }
    } else {
        const nested: { key: string, values: DigitalCorrespondanceTableRow[] }[] = d3.nest<DigitalCorrespondanceTableRow>()
            .key(d => d.parentRowGuid)
            .entries(associationTable);
        nested.forEach(n => {
            // Add missing states at same level
            const digitalSet = digitalSets[n.values[0].digitalSetGuid];
            const toAdd = buildSiblingRows(n, digitalSet);
            allAssociations = allAssociations.concat(toAdd);
            // Add children if none is present
            n.values
                .filter(v => associationTable.filter(a => a.parentRowGuid === v.guid).length <= 0)
                .forEach(v => {
                    const rootLayer = layers.filter(l => l.digitalSetGuid === v.digitalSetGuid);
                    if (rootLayer.length === 1) {
                        const layer = layers.filter(l => l.level === rootLayer[0].level + 1);
                        const values = getChildrenAssociations(layer, digitalSets, v.guid);
                        if (values.length) {
                            allAssociations = allAssociations.concat(values);
                        }
                    }
                });
        });
    }
    return allAssociations;
}

function buildSiblingRows(n: { key: string; values: DigitalCorrespondanceTableRow[]; }, digitalSet: DigitalSet) {
    const siblings = n.values
        .map(c => c.digitalStateCode);
    const missing = digitalSet
        .stateCollection
        .filter(s => siblings.indexOf(s.code) < 0)
        .map(s => {
            return {
                digitalSetGuid: digitalSet.guid,
                digitalStateCode: s.code,
                guid: Helper.getGuid(),
                parentRowGuid: n.key
            };
        });
    const toAdd = n.values.concat(missing);
    toAdd.sort((a, b) => a.digitalStateCode < b.digitalStateCode ? -1 : 1);
    return toAdd;
}

function getChildrenAssociations(layer: DigitalCorrespondanceLayer[], digitalSets: Dictionary<DigitalSet>, parentGuid: string) {
    if (layer.length === 1) {
        const rootSetGuid = layer[0].digitalSetGuid;
        const rootSet = digitalSets[rootSetGuid];
        const stateCollection = rootSet.stateCollection.map(d => d);
        stateCollection.sort((a, b) => a.code < b.code ? -1 : 1);
        const values: DigitalCorrespondanceTableRow[] = stateCollection
            .map(s => {
                return {
                    digitalSetGuid: rootSetGuid,
                    digitalStateCode: s.code,
                    guid: Helper.getGuid(),
                    parentRowGuid: parentGuid
                };
            });
        return values;
    }
    return [];
}

export function mapRowsToViewModels(
    rows: DigitalCorrespondanceTableRow[],
    table: DigitalCorrespondanceTable,
    digitalSets: Dictionary<DigitalSet>) {
    const result: TreeRowViewModel[] = [];
    const roots = rows.filter(n => n.parentRowGuid === guidEmpty);
    roots.forEach(root => {
        const vm = getVM(root, table, digitalSets);
        if (vm !== null) {
            result.push(vm);
            const childrenVms = buildTree(root, rows, table, digitalSets, result);
            childrenVms.forEach(d => {
                vm.search.fullSet = _.uniq(vm.search.fullSet.concat(d.search.fullSet));
                vm.search.fullState = _.uniq(vm.search.fullState.concat(d.search.fullState));
            });
        }
    });
    return result;
}

function buildTree(parent: DigitalCorrespondanceTableRow,
    rows: DigitalCorrespondanceTableRow[],
    table: DigitalCorrespondanceTable,
    digitalSets: Dictionary<DigitalSet>,
    result: TreeRowViewModel[]) {
    const children = rows.filter(r => r.parentRowGuid === parent.guid);
    const vms: TreeRowViewModel[] = [];
    if (children.length) {
        children.forEach(c => {
            const vm = getVM(c, table, digitalSets);
            if (vm !== null) {
                result.push(vm);
                vms.push(vm);
                const childrenVms = buildTree(c, rows, table, digitalSets, result);
                childrenVms.forEach(d => {
                    vm.search.fullSet = _.uniq(vm.search.fullSet.concat(d.search.fullSet));
                    vm.search.fullState = _.uniq(vm.search.fullState.concat(d.search.fullState));
                });
            }
        });
    }
    return vms;
}

function getVM(r: DigitalCorrespondanceTableRow,
    table: DigitalCorrespondanceTable,
    digitalSets: Dictionary<DigitalSet>): TreeRowViewModel | null {
    const level = _.find(table.layers, l => l.digitalSetGuid === r.digitalSetGuid)?.level;
    let vm: TreeRowViewModel;
    if (level !== null && level !== undefined) {
        const digitalSet = digitalSets[r.digitalSetGuid];
        const digitalState = _.find(digitalSet.stateCollection, s => s.code === r.digitalStateCode);
        vm = {
            level,
            digitalSet,
            digitalState: digitalState,
            isChecked: table.associationTable.filter(d => d.guid === r.guid).length > 0,
            model: r,
            search: {
                fullSet: [digitalSet.name],
                fullState: [digitalState.name]
            }
        };
    } else {
        vm = null;
    }
    return vm;
}